import React from 'react';
import { DatePickerValue } from '../../../../../components/date-picker/date-picker';
import { TimePickerValue } from '../../../../../components/time-picker';
import { combineDateAndTime, extractDateAndTime } from '../../../../../utilities';

interface ServiceProviderEventFormInitialValues {
  title: string;
  locationAddress: string;
  responsibleUserId?: string;
  allDay: boolean;
  startAt: Date;
  endAt: Date;
  content: string;
  noteTypeId?: string;
}

export interface ServiceProviderEventFormState {
  title: string;
  locationAddress: string;
  responsibleUserId: string | undefined;
  allDay: boolean;
  startDate: DatePickerValue;
  startTime: TimePickerValue;
  endDate: DatePickerValue;
  endTime: TimePickerValue;
  content: string;
  noteTypeId: string | undefined;
}

export interface ValidationErrors {
  title?: string;
  startDate?: string;
  startTime?: string;
  endDate?: string;
  endTime?: string;
}

export interface ServiceProviderEventFormSubmitValues {
  title: string;
  locationAddress: string;
  responsibleUserId: string;
  allDay: boolean;
  startAt: string;
  endAt: string;
  content?: string;
  noteTypeId?: string;
}

type BuildSubmitValuesResult =
  | { values: ServiceProviderEventFormSubmitValues; error: null }
  | { values: null; error: string | null };

export function useServiceProviderEventFormState(
  initialValues: ServiceProviderEventFormInitialValues
) {
  const extractedStart = extractDateAndTime(initialValues.startAt);
  const extractedEnd = extractDateAndTime(initialValues.endAt);

  const [formState, setFormState] = React.useState<ServiceProviderEventFormState>({
    title: initialValues.title,
    locationAddress: initialValues.locationAddress,
    responsibleUserId: initialValues.responsibleUserId,
    allDay: initialValues.allDay,
    startDate: extractedStart?.date,
    startTime: extractedStart?.time,
    endDate: extractedEnd?.date,
    endTime: extractedEnd?.time,
    content: initialValues.content,
    noteTypeId: initialValues.noteTypeId,
  });

  const [validationErrors, setValidationErrors] = React.useState<ValidationErrors>(
    {}
  );

  function setField<K extends keyof ServiceProviderEventFormState>(
    key: K,
    value: ServiceProviderEventFormState[K]
  ) {
    setFormState(previous => ({ ...previous, [key]: value }));
    if (key in validationErrors) {
      setValidationErrors(previous => ({ ...previous, [key]: undefined }));
    }
  }

  function onAllDayChange(allDay: boolean) {
    setFormState(previous => ({
      ...previous,
      allDay,
      endDate: allDay ? previous.startDate : previous.endDate,
    }));
    if (allDay) {
      setValidationErrors(previous => ({
        ...previous,
        endDate: undefined,
        endTime: undefined,
      }));
    }
  }

  function onStartDateChange(startDate: DatePickerValue) {
    setFormState(previous => ({
      ...previous,
      startDate,
      endDate: previous.allDay ? startDate : previous.endDate,
    }));
    setValidationErrors(previous => ({ ...previous, startDate: undefined }));
  }

  function setStartDateValidationError(error: string | undefined) {
    setValidationErrors(previous => ({ ...previous, startDate: error }));
  }

  function setStartTimeValidationError(error: string | undefined) {
    setValidationErrors(previous => ({ ...previous, startTime: error }));
  }

  function setEndDateValidationError(error: string | undefined) {
    setValidationErrors(previous => ({ ...previous, endDate: error }));
  }

  function setEndTimeValidationError(error: string | undefined) {
    setValidationErrors(previous => ({ ...previous, endTime: error }));
  }

  function validate(): boolean {
    const errors: ValidationErrors = {};

    if (!formState.title.trim()) errors.title = 'Skal udfyldes';
    errors.startDate = !formState.startDate
      ? 'Skal udfyldes'
      : validationErrors.startDate;
    errors.startTime = !formState.startTime
      ? 'Skal udfyldes'
      : validationErrors.startTime;
    errors.endDate = !formState.endDate
      ? 'Skal udfyldes'
      : validationErrors.endDate;
    errors.endTime = !formState.endTime
      ? 'Skal udfyldes'
      : validationErrors.endTime;

    setValidationErrors(errors);
    return Object.values(errors).every(x => !x);
  }

  function buildSubmitValues(): BuildSubmitValuesResult {
    if (!validate()) {
      return { values: null, error: null };
    }

    const startAt = combineDateAndTime(formState.startDate!, formState.startTime!);
    const endAt = combineDateAndTime(formState.endDate!, formState.endTime!);

    if (!startAt || !endAt) {
      return {
        values: null,
        error: 'Start og slut skal være gyldige datoer og tidspunkter.',
      };
    }

    if (endAt <= startAt) {
      return {
        values: null,
        error: 'Sluttidspunkt skal være efter starttidspunkt.',
      };
    }

    return {
      values: {
        title: formState.title.trim(),
        locationAddress: formState.locationAddress.trim(),
        responsibleUserId: formState.responsibleUserId ?? '',
        allDay: formState.allDay,
        startAt: startAt.toISOString(),
        endAt: endAt.toISOString(),
        content: formState.content.trim() || undefined,
        noteTypeId: formState.noteTypeId || undefined,
      },
      error: null,
    };
  }

  return {
    formState,
    validationErrors,
    setField,
    onAllDayChange,
    onStartDateChange,
    setStartDateValidationError,
    setStartTimeValidationError,
    setEndDateValidationError,
    setEndTimeValidationError,
    buildSubmitValues,
  };
}
